import React, { useEffect, useState, useRef } from "react";
import { Loader2 } from "lucide-react";
import ProfileStyle from "../../../assets/stylesheets/dashboard/profile.module.css";
import ToggleStyle from "../../../assets/stylesheets/dashboard/Toggle.module.css";

import DashboardLayout from "../DashboardLayout";
import Logger from "../../../utils/logger";
import {
  getMyPreference,
  updateMyPreference,
  createMyPreference,
} from "../../../api/user-service/preferenceService";

const log = Logger.create("SettingsPage");

export default function SettingsPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [formData, setFormData] = useState({
    language: "en",
    theme: "light",
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    marketingNotifications: false,
    securityNotifications: true,
    activityTracking: false,
    analyticsConsent: false,
  });
  const hasLoadedRef = useRef(false);

  const applyThemeToDocument = (theme) => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const isDark = String(theme).toLowerCase() === "dark";
    root.classList.toggle("dark", isDark);
    root.dataset.theme = isDark ? "dark" : "light";
  };

  useEffect(() => {
    fetchPreference();
  }, []);

  useEffect(() => {
    // Whenever theme changes (load or user selection), apply immediately
    applyThemeToDocument(formData.theme);
  }, [formData.theme]);

  const fetchPreference = async () => {
    try {
      setIsLoading(true);
      const res = await getMyPreference();
      const pref = res?.data ?? res;

      if (!pref) return;

      // Some backends return notifications nested; others return flat keys.
      const notif = pref?.notifications ?? pref ?? {};

      const readBoolean = (obj, key, fallback) => {
        const v = obj?.[key];
        if (v === undefined || v === null) return fallback;
        if (typeof v === "boolean") return v;
        if (typeof v === "number") return v !== 0;
        if (typeof v === "string") {
          const s = v.trim().toLowerCase();
          if (s === "true" || s === "1" || s === "yes" || s === "y" || s === "on") return true;
          if (s === "false" || s === "0" || s === "no" || s === "n" || s === "off") return false;
        }
        // fall back to JS truthiness, but keep it stable
        return Boolean(v);
      };

      const normalizedTheme = (t) => {
        if (!t) return undefined;
        const s = String(t).toUpperCase();
        if (s === "DARK") return "dark";
        if (s === "LIGHT") return "light";
        // backend only allows DARK/LIGHT; default safely
        return "light";
      };

      setFormData((prev) => {
        const next = {
          ...prev,
          language: pref.language ?? prev.language,
          theme: normalizedTheme(pref.theme) ?? prev.theme,
          activityTracking: readBoolean(pref, "activityTracking", prev.activityTracking),
          analyticsConsent: readBoolean(pref, "analyticsConsent", prev.analyticsConsent),
          emailNotifications:
            readBoolean(notif, "email", undefined) ??
            readBoolean(pref, "emailNotifications", prev.emailNotifications),
          pushNotifications:
            readBoolean(notif, "push", undefined) ??
            readBoolean(pref, "pushNotifications", prev.pushNotifications),
          smsNotifications:
            readBoolean(notif, "sms", undefined) ??
            readBoolean(pref, "smsNotifications", prev.smsNotifications),
          marketingNotifications:
            readBoolean(notif, "marketing", undefined) ??
            readBoolean(pref, "marketingNotifications", prev.marketingNotifications),
          securityNotifications:
            readBoolean(notif, "security", undefined) ??
            readBoolean(pref, "securityNotifications", prev.securityNotifications),
        };

        // Apply theme immediately on load
        applyThemeToDocument(next.theme);
        return next;
      });

      hasLoadedRef.current = true;

    } catch (err) {
      // If preferences not created yet, we just show defaults.
      if (err?.response?.status !== 404) {
        log.error("Failed to fetch preferences", err);
      }
      
      hasLoadedRef.current = true;
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "theme") {
        applyThemeToDocument(next.theme);
      }

      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      setSaveError(null);
      const themeEnum = String(formData.theme || "").toUpperCase();
      const payload = {
        language: formData.language,
        // backend expects: DARK | LIGHT
        theme: themeEnum === "DARK" ? "DARK" : "LIGHT",
        emailNotifications: formData.emailNotifications,
        pushNotifications: formData.pushNotifications,
        smsNotifications: formData.smsNotifications,
        marketingNotifications: formData.marketingNotifications,
        securityNotifications: formData.securityNotifications,
        activityTracking: formData.activityTracking,
        analyticsConsent: formData.analyticsConsent,
      };

      try {
        await updateMyPreference(payload);
      } catch (err) {
        // If backend requires creation first
        if (err?.response?.status === 404) {
          await createMyPreference(payload);
        } else {
          throw err;
        }
      }

      alert("Settings saved!");
    } catch (err) {
      log.error("Failed to save settings", err);
      alert("Failed to save settings. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const ToggleRow = ({ label, name, description }) => {
    const on = Boolean(formData[name]);

    return (
      <div className={ToggleStyle["toggleRow"]}>
        <div className={ToggleStyle["toggleText"]}>
          <div className={ToggleStyle["toggleLabel"]}>{label}</div>
          {description ? (
            <div className={ToggleStyle["toggleDescription"]}>{description}</div>
          ) : null}
        </div>

        <button
          type="button"
          className={`${ToggleStyle["toggleButton"]} ${
            on ? ToggleStyle["toggleButtonOn"] : ""
          }`}
          aria-pressed={on}
          aria-label={`${label}: ${on ? "On" : "Off"}`}
          onClick={() => toggle(name)}
          disabled={isSaving}
        >
          <span
            className={`${ToggleStyle["toggleKnob"]} ${
              on ? ToggleStyle["toggleKnobOn"] : ""
            }`}
          />
        </button>
      </div>
    );
  };

  const toggle = (name) => {
    setFormData((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className={ProfileStyle["loading-container"]}>
          <Loader2 className={ProfileStyle["spinner"]} size={32} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <form onSubmit={handleSubmit}>
        <div className={ProfileStyle["section"]}>
          <h2 className={ProfileStyle["section-title"]}>Settings</h2>

          <div className={ProfileStyle["form-grid"]}>
            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>Language</label>
              <div className={ProfileStyle["select-wrapper"]}>
                <select
                  className={ProfileStyle["select"]}
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                >
                  <option value="en">English</option>
                  <option value="fr">French</option>
                </select>
              </div>
            </div>

            <div className={ProfileStyle["form-group"]}>
              <label className={ProfileStyle["label"]}>Theme</label>
              <div className={ProfileStyle["select-wrapper"]}>
                <select
                  className={ProfileStyle["select"]}
                  name="theme"
                  value={formData.theme}
                  onChange={handleChange}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>
            </div>

            {/* Keep toggles in the same grid so they align under Language/Theme */}
             <ToggleRow
              label="Email"
              name="emailNotifications"
              description="Receive notifications by email"
            />
            <ToggleRow
              label="Push"
              name="pushNotifications"
              description="Receive push notifications"
            />
            <ToggleRow
              label="SMS"
              name="smsNotifications"
              description="Receive notifications by SMS"
            />
            <ToggleRow
              label="Marketing"
              name="marketingNotifications"
              description="Promotions, product updates, and marketing messages"
            />
            <ToggleRow
              label="Security"
              name="securityNotifications"
              description="Security-related alerts (login, password changes, etc.)"
            />
            
            <ToggleRow
              label="Activity Tracking"
              name="activityTracking"
              description=""
            />
            <ToggleRow
              label="Analytics Consent"
              name="analyticsConsent"
              description=""
            />
          </div>

          <div className={ProfileStyle["form-footer"]}>
            <button
              type="submit"
              className={ProfileStyle["save-button"]}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2
                    className={`${ProfileStyle["spinner"]} inline mr-2`}
                    size={18}
                  />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </form>
    </DashboardLayout>
  );
}
